import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { FilterType } from './types';

/**
 * Ce qu'il faut savoir d'un filtre pour lire et écrire sa valeur dans l'URL. La `DataTable` les dérive
 * de ses colonnes (`meta.filter`) et de ses `extraFilters`, la `DataView` de sa config.
 */
export type FilterDescriptor = {
    key: string;
    type: FilterType;
    defaultValue?: unknown;
};

type UseFilterStateOptions<TFilter> = {
    filters: FilterDescriptor[];
    enableUrlSync?: boolean;
    /** Clé qui porte la recherche texte dans l'état. */
    searchKey?: string;
    defaultFilters?: Partial<TFilter>;
    /**
     * Scope imposé, tenu HORS de `filterState` : il ne doit ni allumer « Effacer », ni disparaître à la
     * réinitialisation, ni dépendre de l'URL pour survivre à un rechargement.
     */
    pinnedFilters?: Partial<TFilter>;
    /**
     * Paramètres d'URL portés par la page mais qui ne sont pas des filtres — le mode d'affichage
     * courant, par exemple. La synchro réécrit l'INTÉGRALITÉ des paramètres : sans ce passage, un
     * `?view=kanban` posé à côté serait effacé au premier changement de filtre.
     */
    extraParams?: Record<string, string | undefined>;
};

/**
 * Lit UNE valeur d'URL selon le type du filtre. C'est le point unique qui manquait : le parsing était
 * écrit deux fois, typé pour les filtres de colonne et en bloc pour les filtres autonomes — ces
 * derniers récupéraient donc une LISTE DE CHAÎNES pour un `date-range`, un `boolean` ou un `number`.
 * La requête partait quand même (GraphQL coerce), mais la chip ne savait plus s'afficher ni se décocher.
 */
function parseFilterValue(type: FilterType, values: string[]): unknown {
    switch (type) {
        case 'text':
            return values[0] ?? '';
        case 'number': {
            const parsed = parseInt(values[0], 10);
            return Number.isNaN(parsed) ? null : parsed;
        }
        case 'boolean':
            return values[0] === 'true';
        // Les trois rendent une LISTE, même à une seule valeur.
        case 'select':
        case 'multi-select':
        case 'async-multi-select':
            return values;
        case 'date':
            return values[0];
        case 'date-range':
        case 'number-range': {
            const range: Record<string, number> = {};
            for (const value of values) {
                const [operator, bound] = value.split(':');
                if (operator && bound) {
                    range[operator] = parseFloat(bound);
                }
            }
            return Object.keys(range).length > 0 ? range : undefined;
        }
        default:
            return values[0];
    }
}

/** Écrit UNE valeur d'état vers ses paramètres d'URL. Inverse exact de `parseFilterValue`. */
function buildFilterParam(value: unknown): string | string[] | null {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    if (Array.isArray(value)) {
        return value.length > 0 ? (value as string[]) : null;
    }
    if (typeof value === 'object') {
        // date-range / number-range : { gte, lte } → ["gte:123", "lte:456"]
        const parts = Object.entries(value as Record<string, number>)
            .filter(([, bound]) => bound != null)
            .map(([operator, bound]) => `${operator}:${bound}`);
        return parts.length > 0 ? parts : null;
    }
    return String(value);
}

/**
 * L'état des filtres et sa persistance dans l'URL, sans rien devoir à TanStack. Consommé par la
 * `DataView` et, à travers `useDataTableFilters`, par la `DataTable`.
 */
export function useFilterState<TFilter extends Record<string, unknown>>({
    filters,
    enableUrlSync = false,
    searchKey = 'searchValue',
    defaultFilters,
    pinnedFilters,
    extraParams,
}: UseFilterStateOptions<TFilter>) {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const searchParams = enableUrlSync ? urlSearchParams : null;
    const setSearchParams = enableUrlSync ? setUrlSearchParams : null;

    const parseUrlParams = useCallback(
        (params: URLSearchParams): Partial<TFilter> => {
            const state: Record<string, unknown> = {};

            if (params.has(searchKey)) {
                state[searchKey] = params.get(searchKey) || '';
            }

            for (const filter of filters) {
                const values = params.getAll(filter.key);
                if (values.length === 0) {
                    continue;
                }
                const parsed = parseFilterValue(filter.type, values);
                if (parsed !== undefined) {
                    state[filter.key] = parsed;
                }
            }

            return state as Partial<TFilter>;
        },
        [filters, searchKey],
    );

    const buildUrlParams = useCallback((state: Partial<TFilter>): Record<string, string | string[]> => {
        const params: Record<string, string | string[]> = {};
        for (const [key, value] of Object.entries(state)) {
            const param = buildFilterParam(value);
            if (param !== null) {
                params[key] = param;
            }
        }
        return params;
    }, []);

    // `isRestoredFromUrl` : la requête initiale d'une page ne lit pas l'URL, une table serveur doit donc la refaire.
    const [initialState] = useState<{ filters: Partial<TFilter>; isRestoredFromUrl: boolean }>(() => {
        const declaredDefaults: Record<string, unknown> = {};
        for (const filter of filters) {
            if (filter.defaultValue !== undefined) {
                declaredDefaults[filter.key] = filter.defaultValue;
            }
        }

        if (enableUrlSync && searchParams) {
            const fromUrl = parseUrlParams(searchParams);
            if (Object.keys(fromUrl).length > 0) {
                // L'URL prime CLÉ PAR CLÉ, pas en bloc : un filtre à valeur par défaut — le périmètre
                // « En cours » — disparaissait dès qu'un autre paramètre traînait dans l'URL. Un lien
                // profond qui veut lever ce périmètre écrit sa valeur (`isCurrent=false`), il ne compte
                // pas sur son absence.
                return { filters: { ...declaredDefaults, ...fromUrl } as Partial<TFilter>, isRestoredFromUrl: true };
            }
        }
        if (defaultFilters) {
            return { filters: defaultFilters, isRestoredFromUrl: false };
        }

        return { filters: declaredDefaults as Partial<TFilter>, isRestoredFromUrl: false };
    });
    const [filterState, setFilterState] = useState<Partial<TFilter>>(initialState.filters);

    // Ce qui part au serveur : le scope imposé, puis les filtres de l'utilisateur (qui peuvent l'affiner,
    // jamais le lever). `filterState` reste la vue « ce que l'utilisateur a posé », seule à piloter l'UI.
    // Sérialisé pour comparer le scope PAR VALEUR : les appelants le construisent en ligne (`{ siteId }`),
    // une identité d'objet neuve à chaque rendu relancerait un refetch en boucle.
    const pinnedFiltersKey = JSON.stringify(pinnedFilters ?? {});
    const serverFilters = useMemo(
        () => ({ ...(JSON.parse(pinnedFiltersKey) as Partial<TFilter>), ...filterState }),
        [pinnedFiltersKey, filterState],
    );

    // Sérialisé pour la même raison que `pinnedFilters` : construit en ligne par l'appelant, une identité
    // neuve à chaque rendu relancerait l'effet de synchro en boucle.
    const extraParamsKey = JSON.stringify(extraParams ?? {});

    const searchValue = (filterState[searchKey as keyof TFilter] as string) || '';

    useEffect(() => {
        if (!enableUrlSync || !setSearchParams) {
            return;
        }
        // Le scope est réécrit dans l'URL : au rechargement, `parseUrlParams` ignore les clés qui ne
        // correspondent à aucun filtre déclaré, c'est le parent qui le relit et le repasse en `pinnedFilters`.
        const params = buildUrlParams(serverFilters);
        const newSearchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (Array.isArray(value)) {
                for (const item of value) {
                    newSearchParams.append(key, item);
                }
            } else {
                newSearchParams.set(key, value);
            }
        }
        for (const [key, value] of Object.entries(JSON.parse(extraParamsKey) as Record<string, string | undefined>)) {
            if (value !== undefined) {
                newSearchParams.set(key, value);
            }
        }
        setSearchParams(newSearchParams, { replace: true });
    }, [serverFilters, enableUrlSync, setSearchParams, buildUrlParams, extraParamsKey]);

    const updateFilter = useCallback((key: string, value: unknown) => {
        setFilterState((previous) => {
            if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
                const next = { ...previous };
                delete next[key as keyof TFilter];
                return next;
            }
            return { ...previous, [key]: value };
        });
    }, []);

    const updateSearch = useCallback(
        (value: string) => {
            updateFilter(searchKey, value || undefined);
        },
        [searchKey, updateFilter],
    );

    const clearFilters = useCallback(() => setFilterState({} as Partial<TFilter>), []);

    return {
        filterState,
        isRestoredFromUrl: initialState.isRestoredFromUrl,
        serverFilters,
        setFilterState,
        searchValue,
        searchKey,
        updateSearch,
        updateFilter,
        clearFilters,
    };
}
